import { useEffect, useMemo, useState } from 'react';
import { assemble } from '../lib/pdfEngine';
import { bytesFor } from '../lib/documentStore';
import { renderPdfBytes } from '../lib/thumbnails';
import {
  DEFAULT_PAGE_NUMBERS,
  DEFAULT_WATERMARK,
  type StampOptions,
  type StampPosition,
} from '../lib/stamps';
import type { PageItem } from '../lib/types';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from './Icons';

const POSITIONS: Array<{ value: StampPosition; label: string }> = [
  { value: 'bottom-center', label: 'Bottom center' },
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'top-center', label: 'Top center' },
  { value: 'top-right', label: 'Top right' },
  { value: 'top-left', label: 'Top left' },
];

interface ExportOptionsDialogProps {
  pages: PageItem[];
  stamps: StampOptions;
  onChange: (stamps: StampOptions) => void;
  onClose: () => void;
}

/**
 * Page numbers and watermarks, with a preview of the real thing.
 *
 * The preview assembles an actual one-page PDF with the settings applied and
 * renders it, rather than drawing an approximation — an approximation can drift
 * from the output and then it is worse than no preview at all.
 */
export function ExportOptionsDialog({
  pages,
  stamps,
  onChange,
  onClose,
}: ExportOptionsDialogProps) {
  const [pageIndex, setPageIndex] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * Settings live here, with separate on/off flags, so switching a section off
   * keeps its values and its fields on screen — greyed rather than gone. A
   * section that vanishes takes the layout with it and loses what was typed.
   *
   * Both start on, so opening this shows what the options do rather than two
   * collapsed checkboxes over a bare page.
   */
  const [numbersOn, setNumbersOn] = useState(true);
  const [watermarkOn, setWatermarkOn] = useState(true);
  const [numbers, setNumbersState] = useState({ ...DEFAULT_PAGE_NUMBERS, ...stamps.pageNumbers });
  const [watermark, setWatermarkState] = useState({ ...DEFAULT_WATERMARK, ...stamps.watermark });

  const page = pages[Math.min(pageIndex, pages.length - 1)];

  // The parent only ever stores what is actually switched on.
  useEffect(() => {
    onChange({
      pageNumbers: numbersOn ? numbers : undefined,
      watermark: watermarkOn ? watermark : undefined,
    });
    // onChange identity changes every render in the parent; depending on it
    // would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbersOn, watermarkOn, numbers, watermark]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);


  /*
   * The preview shows one page out of context, so the numbering has to be
   * worked out here: {N} is the real total, and the start number is advanced to
   * this page's position. Otherwise every page would preview as "1 of 1".
   */
  const previewStamps = useMemo<StampOptions>(() => {
    const activeWatermark = watermarkOn ? watermark : undefined;
    if (!numbersOn) return { watermark: activeWatermark };
    const numbered = numbers.skipFirstPage ? pages.slice(1) : pages;
    const positionInNumbered = numbered.findIndex((p) => p.uid === page?.uid);
    const isNumbered = positionInNumbered !== -1;
    return {
      watermark: activeWatermark,
      pageNumbers: isNumbered
        ? {
            ...numbers,
            format: numbers.format.replace(/\{N\}/g, String(numbers.startAt + numbered.length - 1)),
            startAt: numbers.startAt + positionInNumbered,
            skipFirstPage: false,
          }
        : undefined,
    };
  }, [numbersOn, numbers, watermarkOn, watermark, pages, page?.uid]);

  // Debounced so dragging the opacity slider does not queue a render per pixel.
  useEffect(() => {
    if (!page) return;
    let cancelled = false;
    setRendering(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const bytes = await assemble([page], bytesFor, {}, previewStamps);
          const url = await renderPdfBytes(bytes);
          if (!cancelled) {
            setPreview(url);
            setFailed(false);
          }
        } catch {
          if (!cancelled) setFailed(true);
        } finally {
          if (!cancelled) setRendering(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [page, previewStamps]);

  const setNumbers = (next: Partial<typeof DEFAULT_PAGE_NUMBERS>) =>
    setNumbersState((current) => ({ ...current, ...next }));
  const setWatermark = (next: Partial<typeof DEFAULT_WATERMARK>) =>
    setWatermarkState((current) => ({ ...current, ...next }));

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="dialog options" role="dialog" aria-modal="true" aria-label="Export options">
        <button className="icon-btn options__close" onClick={onClose} aria-label="Close">
          <XIcon />
        </button>

        <div className="options__form">
          <h2>Export options</h2>
          <p className="dialog__hint">Applied when you export. Your working copy is unchanged.</p>

          <div className="dialog__section">
            <label className="dialog__toggle">
              <input
                type="checkbox"
                checked={numbersOn}
                onChange={(event) => setNumbersOn(event.target.checked)}
              />
              <span>Page numbers</span>
            </label>

            <fieldset disabled={!numbersOn} className={numbersOn ? 'dialog__nested' : 'dialog__nested is-off'}>
                <label className="field">
                  <span className="field__label">Format</span>
                  <input
                    type="text"
                    value={numbers.format}
                    onChange={(event) => setNumbers({ format: event.target.value })}
                  />
                  <span className="field__hint">
                    <code>{'{n}'}</code> the page, <code>{'{N}'}</code> the last one. Bates:{' '}
                    <code>ABC-{'{n}'}</code> with a start number.
                  </span>
                </label>
                <div className="field-row">
                  <label className="field">
                    <span className="field__label">Position</span>
                    <select
                      value={numbers.position}
                      onChange={(event) =>
                        setNumbers({ position: event.target.value as StampPosition })
                      }
                    >
                      {POSITIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field__label">Start at</span>
                    <input
                      type="number"
                      min={0}
                      value={numbers.startAt}
                      onChange={(event) =>
                        setNumbers({ startAt: Number(event.target.value) || 0 })
                      }
                    />
                  </label>
                </div>
                <label className="dialog__toggle">
                  <input
                    type="checkbox"
                    checked={numbers.skipFirstPage}
                    onChange={(event) => setNumbers({ skipFirstPage: event.target.checked })}
                  />
                  <span>Don’t number the first page (cover)</span>
                </label>
              </fieldset>
          </div>

          <div className="dialog__section">
            <label className="dialog__toggle">
              <input
                type="checkbox"
                checked={watermarkOn}
                onChange={(event) => setWatermarkOn(event.target.checked)}
              />
              <span>Watermark</span>
            </label>

            <fieldset disabled={!watermarkOn} className={watermarkOn ? 'dialog__nested' : 'dialog__nested is-off'}>
                <div className="field-row">
                  <label className="field">
                    <span className="field__label">Type</span>
                    <select
                      value={watermark.kind}
                      onChange={(event) =>
                        setWatermark({ kind: event.target.value as 'text' | 'logo' | 'both' })
                      }
                    >
                      <option value="text">Text</option>
                      <option value="logo">ON Services logo</option>
                      <option value="both">Logo with text</option>
                    </select>
                  </label>
                  {watermark.kind !== 'logo' && (
                    <label className="field">
                      <span className="field__label">Text</span>
                      <input
                        type="text"
                        value={watermark.text}
                        onChange={(event) => setWatermark({ text: event.target.value })}
                        placeholder="DRAFT"
                      />
                    </label>
                  )}
                </div>
                <div className="field-row">
                  {/* The mark is always upright — a tilted logo reads as a
                      mistake rather than a watermark. */}
                  {watermark.kind === 'text' ? (
                    <label className="field">
                      <span className="field__label">Angle</span>
                      <select
                        value={watermark.angle}
                        onChange={(event) => setWatermark({ angle: Number(event.target.value) })}
                      >
                        <option value={45}>Diagonal</option>
                        <option value={0}>Horizontal</option>
                        <option value={90}>Vertical</option>
                      </select>
                    </label>
                  ) : (
                    <span />
                  )}
                  <label className="field">
                    <span className="field__label">
                      Opacity — {Math.round(watermark.opacity * 100)}%
                    </span>
                    <input
                      type="range"
                      min={5}
                      max={60}
                      value={Math.round(watermark.opacity * 100)}
                      onChange={(event) =>
                        setWatermark({ opacity: Number(event.target.value) / 100 })
                      }
                    />
                  </label>
                </div>
              </fieldset>
          </div>

          <div className="dialog__actions">
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>

        <div className="options__preview">
          <div className="options__preview-stage">
            {preview ? (
              <img src={preview} alt="Preview of the exported page" className={rendering ? 'is-stale' : undefined} />
            ) : (
              <p className="options__preview-status">
                {failed ? 'This page could not be previewed.' : 'Rendering…'}
              </p>
            )}
          </div>
          <div className="options__preview-bar">
            <button
              className="icon-btn"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex === 0}
              aria-label="Previous page"
            >
              <ChevronLeftIcon size={16} />
            </button>
            <span>
              Page {Math.min(pageIndex, pages.length - 1) + 1} of {pages.length}
            </span>
            <button
              className="icon-btn"
              onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))}
              disabled={pageIndex >= pages.length - 1}
              aria-label="Next page"
            >
              <ChevronRightIcon size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
